import './faq.scss';
import autoComplete from 'pixabay-javascript-autocomplete';
import { getOffset, smoothScrollTo } from './../../utilities/js/helper';

class Faq {
    constructor(element) {
        const defaults = {
            initAttr: 'data-faq',
            categoryAttr: 'category',
            entryAttr: 'entry',
            categorySelector: 'data-faq-category',
            navigationAttr: 'navigation',
            formAttr: 'form',
            autocompleteAttr: 'autocomplete'
        };

        this.settings = Object.assign({}, defaults);

        this.$faq = element;
        this.$faqForm = this.$faq.querySelector('[' + this.settings.initAttr + '="' + this.settings.formAttr + '"]');
        this.$faqAutocomplete = this.$faq.querySelector('[' + this.settings.initAttr + '="' + this.settings.autocompleteAttr + '"]');
        this.$faqCategories = this.$faq.querySelectorAll('[' + this.settings.initAttr + '="' + this.settings.categoryAttr + '"]');
        this.$faqNavigation = this.$faq.querySelector('[' + this.settings.initAttr + '="' + this.settings.navigationAttr + '"]');
        this.$faqEntries = this.$faq.querySelectorAll('[' + this.settings.initAttr + '="' + this.settings.entryAttr + '"]');
    }

    initialize() {
        if (this.$faqForm !== null && this.$faqAutocomplete !== null) {
            this.initFaqForm();
        }

        if (this.$faqCategories.length > 0 && this.$faqNavigation !== null) {
            this.initFaqCategories();
        }
    }

    initFaqCategories() {
        const setActive = (element, id) => {
            const $items = this.$faqNavigation.querySelectorAll('label');

            $items.forEach(($item) => {
                $item.classList.remove('is--active');
                $item.parentElement.setAttribute('aria-selected', false);
            });

            this.$faqCategories.forEach(($category) => {
                $category.parentElement.hidden = true;
            });

            for (let i = 0; i < $items.length; i++) {
                const $item = $items[i];

                if ($item.getAttribute('for') === id) {
                    element.parentElement.hidden = false;
                    $item.classList.add('is--active');
                    $item.parentElement.setAttribute('aria-selected', true);

                    const elementTop = getOffset(element).top;
                    smoothScrollTo(window.scrollX || 0, elementTop);
                }
            }
        };

        const setActiveEntry = (id) => {
            for (let i = 0; i < this.$faqEntries.length; i++) {
                const $entry = this.$faqEntries[i];
                const $entryContainer = $entry.closest('[data-faq="item"]');
                const $entryButton = $entryContainer?.querySelector('[data-faq-entry="button"]');
                const $entryContent = $entryContainer?.querySelector('[data-faq-entry="content"]');

                if ($entry.id !== id) {
                    $entry.checked = false;
                    $entryButton?.setAttribute('aria-expanded', false);
                    $entryContent?.setAttribute('aria-hidden', true);
                }

                if ($entry.checked == true) {
                    $entryButton?.setAttribute('aria-expanded', true);
                    $entryContent?.setAttribute('aria-hidden', false);

                    setTimeout(() => {
                        const elementTop = getOffset($entry).top;
                        smoothScrollTo(window.scrollX || 0, elementTop);
                    }, 50);
                }
            }
        };

        this.$faq.addEventListener('change', (e) => {
            if (e.target.getAttribute(this.settings.initAttr) === this.settings.categoryAttr) {
                setActive(e.target, e.target.id);
            }

            if (e.target.getAttribute(this.settings.initAttr) === this.settings.entryAttr) {
                setActiveEntry(e.target.id);
            }
        });

        this.$faq.addEventListener('click', (e) => {
            if (e.target.getAttribute(this.settings.initAttr) === this.settings.entryAttr) {
                if (e.target.getAttribute(this.settings.initAttr) === this.settings.entryAttr) {
                    const isChecked = e.target.checked;
                    const $entryContainer = e.target.closest('[data-faq="item"]');
                    const $entryButton = $entryContainer?.querySelector('[data-faq-entry="button"]');
                    const $entryContent = $entryContainer?.querySelector('[data-faq-entry="content"]');

                    if (!isChecked) {
                        $entryButton?.setAttribute('aria-expanded', false);
                        $entryContent?.setAttribute('aria-hidden', true);
                    }
                }
            }
        });

        this.$faq.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && e.target.getAttribute('data-faq-entry') === 'button') {
                e.preventDefault();

                const $button = e.target;
                const $entryContainer = $button.closest('[data-faq="item"]');
                const $checkbox = $button.previousElementSibling;
                const $entryContent = $entryContainer.querySelector('[data-faq-entry="content"]');

                if ($checkbox && $checkbox.getAttribute(this.settings.initAttr) === this.settings.entryAttr) {
                    $checkbox.checked = !$checkbox.checked;

                    const changeEvent = new Event('change', {
                        bubbles: true
                    });
                    $checkbox.dispatchEvent(changeEvent);
                }

                if (!$checkbox.checked) {
                    $button?.setAttribute('aria-expanded', false);
                    $entryContent?.setAttribute('aria-hidden', true);
                }
            }
        });
    }

    async initFaqForm() {
        const CustomAutoComplete = autoComplete;

        const formAction = this.$faqForm.getAttribute('action');
        const formMethod = (this.$faqForm.getAttribute('method') || 'GET').toUpperCase();

        try {
            const formData = new FormData(this.$faqForm);

            const response = await fetch(formAction, {
                method: formMethod,
                body: formMethod === 'POST' ? formData : null
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const faqData = await response.json();

            this.autocompleteField = new CustomAutoComplete({
                selector: this.$faqAutocomplete,
                renderItem: (item, search) => {
                    search = search.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
                    const re = new RegExp(`(${search})`, 'gi');
                    return `<button class="autocomplete-suggestion autocomplete-suggestion__entry" data-val="${item.title}" data-val-id="${item.id}">${item.title.replace(re, '<strong>$1</strong>')}</button>`;
                },
                source: (term, suggest) => {
                    term = term.toLowerCase();
                    const matchesTitles = faqData.filter(item => item.title.toLowerCase().includes(term));
                    const matchesContents = faqData.filter(item => item.content.toLowerCase().includes(term));
                    suggest([...matchesTitles, ...matchesContents]);
                },
                minChars: 2,
                onSelect: (event, term, item) => {
                    const entryId = item.getAttribute('data-val-id');
                    const $entry = document.getElementById(entryId);
                    if ($entry) {
                        $entry.checked = true;
                        $entry.dispatchEvent(new Event('change', { bubbles: true }));

                        const categoryId = $entry.getAttribute(this.settings.categorySelector);
                        const $category = document.getElementById(categoryId);

                        if ($category) {
                            $category.checked = true;
                            $category.dispatchEvent(new Event('change', { bubbles: true }));
                        }
                    }
                }
            });
        } catch (error) {
            console.error('An error occurred while fetching FAQ data:', error);
        }

        this.$faqForm.addEventListener('submit', (e) => {
            e.preventDefault();
        });
    }
}

export {Faq};

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $faq = $context.querySelectorAll('[data-faq="root"]');

        for (let i = 0; i < $faq.length; i++) {
            const $$faq = new Faq($faq[i]);
            $$faq.initialize();
        }
    }
});
